import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { getOnboardingResumeTarget, readPendingInviteFromSession, hasRealOrgId } from '@/utils/onboardingResume';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthType } = useAuthStore();
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const handleCallback = async () => {
      try {
        // Wait for Supabase to process the OAuth hash
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (!session?.user) {
          // No session — try listening for the auth state change (OAuth hash processing)
          const { data } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (!mounted) return;
            if (event === 'SIGNED_IN' && sess?.user) {
              data.subscription.unsubscribe();
              await resolveRedirect(sess.user);
            }
          });

          // Timeout fallback — if no auth event fires
          setTimeout(() => {
            if (mounted) {
              setAuthType('signin');
              navigate('/');
            }
          }, 5000);
          return;
        }

        await resolveRedirect(session.user);
      } catch (err) {
        console.error('Auth callback error:', err);
        if (mounted) {
          setError(err.message);
          setTimeout(() => navigate('/'), 3000);
        }
      }
    };

    const resolveRedirect = async (user) => {
      if (!mounted) return;

      // Route via the resume helper — one source of truth for
      // "where does this user belong?" shared with SignIn and
      // EmailConfirmation. Previously this file duplicated the
      // status-check logic and had a divergent set of messages
      // (ONBOARDING_STARTED vs ONBOARDING_COMPLETED); now every
      // path funnels through the same helper.
      try {
        const profile = await apiClient.getUserByUserId(user.id).catch(() => null);
        const organization = hasRealOrgId(profile)
          ? await apiClient.getOrganizationById(profile.org_id).catch(() => null)
          : null;
        const { data: { session } } = await supabase.auth.getSession();
        const target = getOnboardingResumeTarget(profile, organization, {
          pendingInvite: readPendingInviteFromSession(session),
        });
        // Refresh the auth store's cached profile so downstream pages
        // (dashboard, MFA, etc.) don't have to re-fetch.
        if (target.path === '/dashboard') {
          await useAuthStore.getState().fetchUserProfile();
        }
        navigate(target.path);
      } catch (error) {
        console.error('AuthCallback: resume routing failed', error);
        // Fail-safe → /onboarding step 1 so the next round can
        // re-resolve rather than stranding the user on a 403 loop.
        navigate('/onboarding');
      }
    };

    handleCallback();

    return () => {
      mounted = false;
    };
  }, [navigate, setAuthType]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <p className="text-red-500 text-sm">{error}</p>
        ) : (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}
